/**
 * System Health Hook
 * 
 * Provides real-time monitoring of the social media posting system health,
 * including edge function status, processing queue, and system metrics.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { POST_STATUS } from '@/constants/socialPostStatus'
import { format, addMinutes, isAfter, differenceInSeconds } from 'date-fns'
import type { RealtimeChannel } from '@supabase/supabase-js'

// Types
export interface SystemHealth {
  edgeFunction: {
    lastRun: Date | null
    nextRun: Date | null
    lastRunStatus: 'success' | 'error' | 'unknown'
    postsProcessed: number
    errors: string[]
    isOverdue: boolean
  }
  queue: {
    scheduled: number
    processing: number
    nextHour: number
    overdue: number
  }
  metrics: {
    postsToday: number
    postsThisWeek: number
    postsThisMonth: number
    successRate: number
    averageProcessingTime: number
    failedPosts: number
  }
  recentActivity: StatusChange[]
  connection: {
    supabase: 'connected' | 'disconnected' | 'connecting'
    realtime: 'connected' | 'disconnected' | 'connecting'
    lastSync: Date | null
  }
}

export interface StatusChange {
  id: string
  post_id: string
  caption_preview: string
  old_status: string
  new_status: string
  changed_at: string
  error_message?: string
}

export interface EdgeFunctionLog {
  id: string
  function_name: string
  status: 'success' | 'error'
  posts_processed: number
  error_message?: string
  execution_time_ms: number
  created_at: string
}

// Helper function to get the start of today/week/month
function getDateRanges() {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const weekStart = new Date(today)
  weekStart.setDate(today.getDate() - today.getDay())
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1)

  return { today, weekStart, monthStart }
}

// Main hook
export function useSystemHealth() {
  const queryClient = useQueryClient()
  const [realtimeChannel, setRealtimeChannel] = useState<RealtimeChannel | null>(null)
  const [connectionStatus, setConnectionStatus] = useState<{
    supabase: 'connected' | 'disconnected' | 'connecting'
    realtime: 'connected' | 'disconnected' | 'connecting'
  }>({
    supabase: 'connecting',
    realtime: 'disconnected'
  })

  // Fetch edge function logs
  const fetchEdgeFunctionStatus = async () => {
    // Get the most recent edge function execution log
    const { data: logs, error } = await supabase
      .from('edge_function_logs')
      .select('*')
      .eq('function_name', 'process-scheduled-posts')
      .order('created_at', { ascending: false })
      .limit(10)

    if (error) {
      console.error('Error fetching edge function logs:', error)
      return {
        lastRun: null,
        nextRun: null,
        lastRunStatus: 'unknown' as const,
        postsProcessed: 0,
        errors: [],
        isOverdue: false
      }
    }

    const lastLog = logs?.[0]
    if (!lastLog) {
      return {
        lastRun: null,
        nextRun: null,
        lastRunStatus: 'unknown' as const,
        postsProcessed: 0,
        errors: [],
        isOverdue: true
      }
    }

    const lastRun = new Date(lastLog.created_at)
    const nextRun = addMinutes(lastRun, 10) // Edge function runs every 10 minutes
    const isOverdue = isAfter(new Date(), addMinutes(nextRun, 5)) // Consider overdue if 5 minutes late

    // Collect errors from recent runs
    const recentErrors = logs
      .filter(log => log.status === 'error' && log.error_message)
      .slice(0, 3)
      .map(log => log.error_message!)

    return {
      lastRun,
      nextRun,
      lastRunStatus: lastLog.status,
      postsProcessed: lastLog.posts_processed || 0,
      errors: recentErrors,
      isOverdue
    }
  }

  // Fetch queue status
  const fetchQueueStatus = async () => {
    const now = new Date()
    const oneHourFromNow = new Date(now.getTime() + 60 * 60 * 1000)

    // Count posts by status
    const { data: statusCounts } = await supabase
      .from('social_posts')
      .select('status', { count: 'exact', head: true })
      .in('status', [POST_STATUS.SCHEDULED, POST_STATUS.PROCESSING])

    // Count posts scheduled for the next hour
    const { count: nextHourCount } = await supabase
      .from('social_posts')
      .select('*', { count: 'exact', head: true })
      .eq('status', POST_STATUS.SCHEDULED)
      .gte('scheduled_time', now.toISOString())
      .lte('scheduled_time', oneHourFromNow.toISOString())

    // Count overdue posts
    const { count: overdueCount } = await supabase
      .from('social_posts')
      .select('*', { count: 'exact', head: true })
      .eq('status', POST_STATUS.SCHEDULED)
      .lt('scheduled_time', now.toISOString())

    // Get actual counts by status
    const { count: scheduledCount } = await supabase
      .from('social_posts')
      .select('*', { count: 'exact', head: true })
      .eq('status', POST_STATUS.SCHEDULED)

    const { count: processingCount } = await supabase
      .from('social_posts')
      .select('*', { count: 'exact', head: true })
      .eq('status', POST_STATUS.PROCESSING)

    return {
      scheduled: scheduledCount || 0,
      processing: processingCount || 0,
      nextHour: nextHourCount || 0,
      overdue: overdueCount || 0
    }
  }

  // Fetch system metrics
  const fetchMetrics = async () => {
    const { today, weekStart, monthStart } = getDateRanges()

    // Count posts today
    const { count: todayCount } = await supabase
      .from('social_posts')
      .select('*', { count: 'exact', head: true })
      .gte('created_at', today.toISOString())

    // Count posts this week
    const { count: weekCount } = await supabase
      .from('social_posts')
      .select('*', { count: 'exact', head: true })
      .gte('created_at', weekStart.toISOString())

    // Count posts this month
    const { count: monthCount } = await supabase
      .from('social_posts')
      .select('*', { count: 'exact', head: true })
      .gte('created_at', monthStart.toISOString())

    // Calculate success rate (posted vs error in last 7 days)
    const { count: successCount } = await supabase
      .from('social_posts')
      .select('*', { count: 'exact', head: true })
      .eq('status', POST_STATUS.POSTED)
      .gte('published_at', weekStart.toISOString())

    const { count: errorCount } = await supabase
      .from('social_posts')
      .select('*', { count: 'exact', head: true })
      .eq('status', POST_STATUS.ERROR)
      .gte('updated_at', weekStart.toISOString())

    const totalProcessed = (successCount || 0) + (errorCount || 0)
    const successRate = totalProcessed > 0 ? ((successCount || 0) / totalProcessed) * 100 : 100

    // Count current failed posts
    const { count: failedCount } = await supabase
      .from('social_posts')
      .select('*', { count: 'exact', head: true })
      .eq('status', POST_STATUS.ERROR)

    // Calculate average processing time from edge function logs
    const { data: processingLogs } = await supabase
      .from('edge_function_logs')
      .select('execution_time_ms')
      .eq('function_name', 'process-scheduled-posts')
      .eq('status', 'success')
      .order('created_at', { ascending: false })
      .limit(20)

    const avgProcessingTime = processingLogs && processingLogs.length > 0
      ? processingLogs.reduce((sum, log) => sum + (log.execution_time_ms || 0), 0) / processingLogs.length
      : 0

    return {
      postsToday: todayCount || 0,
      postsThisWeek: weekCount || 0,
      postsThisMonth: monthCount || 0,
      successRate: Math.round(successRate * 10) / 10,
      averageProcessingTime: Math.round(avgProcessingTime),
      failedPosts: failedCount || 0
    }
  }

  // Fetch recent activity
  const fetchRecentActivity = async (): Promise<StatusChange[]> => {
    // Create a temporary activity log from post status changes
    const { data: posts } = await supabase
      .from('social_posts')
      .select('id, caption, status, updated_at, error_message, processing_logs')
      .order('updated_at', { ascending: false })
      .limit(10)

    if (!posts) return []

    // Transform posts into status changes
    return posts.map(post => ({
      id: `${post.id}-${post.updated_at}`,
      post_id: post.id,
      caption_preview: post.caption ? post.caption.substring(0, 50) + (post.caption.length > 50 ? '...' : '') : 'No caption',
      old_status: 'UNKNOWN', // We don't have historical data yet
      new_status: post.status,
      changed_at: post.updated_at,
      error_message: post.error_message
    }))
  }

  // Main query
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['system-health'],
    queryFn: async (): Promise<SystemHealth> => {
      const [edgeFunction, queue, metrics, recentActivity] = await Promise.all([
        fetchEdgeFunctionStatus(),
        fetchQueueStatus(),
        fetchMetrics(),
        fetchRecentActivity()
      ])

      return {
        edgeFunction,
        queue,
        metrics,
        recentActivity,
        connection: {
          supabase: connectionStatus.supabase,
          realtime: connectionStatus.realtime,
          lastSync: new Date()
        }
      }
    },
    refetchInterval: 30000 // Auto-refresh every 30 seconds
  })

  // Set up real-time subscriptions
  useEffect(() => {
    // Check Supabase connection
    const checkConnection = async () => {
      try {
        const { error } = await supabase.from('social_posts').select('id').limit(1)
        setConnectionStatus(prev => ({ ...prev, supabase: error ? 'disconnected' : 'connected' }))
      } catch {
        setConnectionStatus(prev => ({ ...prev, supabase: 'disconnected' }))
      }
    }

    checkConnection()

    // Set up real-time subscription
    const channel = supabase
      .channel('system-health')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'social_posts'
      }, () => {
        // Invalidate and refetch on any post changes
        queryClient.invalidateQueries({ queryKey: ['system-health'] })
      })
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'edge_function_logs'
      }, () => {
        // Invalidate and refetch on new edge function logs
        queryClient.invalidateQueries({ queryKey: ['system-health'] })
      })

    // Subscribe and track connection status
    channel.subscribe((status) => {
      setConnectionStatus(prev => ({
        ...prev,
        realtime: status === 'SUBSCRIBED' ? 'connected' : 
                  status === 'CHANNEL_ERROR' ? 'disconnected' : 'connecting'
      }))
    })

    setRealtimeChannel(channel)

    return () => {
      if (channel) {
        supabase.removeChannel(channel)
      }
    }
  }, [queryClient])

  return {
    data,
    isLoading,
    error,
    refetch,
    isConnected: connectionStatus.supabase === 'connected' && connectionStatus.realtime === 'connected'
  }
}

// Hook to manually process scheduled posts (for testing)
export function useManualProcess() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      // Trigger the edge function manually
      const { data, error } = await supabase.functions.invoke('process-scheduled-posts', {
        body: { manual: true }
      })

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-health'] })
    }
  })
}